import { Card } from '@aimsos/ui';

/**
 * Shared chrome for the hero and compact workspace cards.
 *
 * The whole card is one click target, but the studio chips inside it are real
 * buttons too — so instead of a `role="button"` wrapper (which would nest
 * interactive elements and give screen readers a button inside a button) the
 * card renders a single stretched `<button>` behind its content. Content sits
 * above it and is `pointer-events-none` except for the slots that opt back in
 * via `pointer-events-auto` (the chips).
 *
 * `onSelect` is fired by that stretched button, so keyboard focus, Enter/Space
 * and the focus ring all come from the platform rather than a hand-rolled
 * `onKeyDown`.
 *
 * Two distinct blocked states (CT-4940):
 * - `busy`: THIS card is the one switching — spinner, dimmed, wait cursor.
 * - `disabled`: a switch is in flight on some OTHER card. The click target is
 *   inert and the hover elevation is suppressed, so the card stops inviting a
 *   press that `openWorkspace` would silently drop.
 */
function WorkspaceCardFrame({
  label,
  active = false,
  busy = false,
  disabled = false,
  onSelect,
  className = '',
  children,
}) {
  const inert = busy || disabled;

  return (
    <Card
      className={[
        'group relative rounded-2xl shadow-none transition-all duration-150',
        // `!` on background/border: Card ships `border-edge bg-surface` in its
        // own base classes and, being plain utilities in the same layer, they
        // win the cascade over these — the active card silently rendered as a
        // plain white/neutral card with no brand highlight at all.
        active ? '!border-brand-500/40 !bg-brand-500/[0.06]' : '!bg-surface/40',
        // Hover elevation (CT-4940). Suppressed while any switch is in flight.
        !inert &&
          (active
            ? 'hover:!border-brand-500/60 hover:shadow-lg hover:shadow-brand-500/10'
            : 'hover:!border-edge-strong hover:!bg-surface-hover hover:shadow-lg'),
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        aria-label={label}
        disabled={inert}
        onClick={() => onSelect?.()}
        className={[
          'absolute inset-0 rounded-2xl outline-none',
          'focus-visible:ring-2 focus-visible:ring-brand-500/60',
          busy ? 'cursor-wait' : disabled ? 'cursor-default' : 'cursor-pointer',
        ].join(' ')}
      />
      <div className={['pointer-events-none relative', busy ? 'opacity-70' : ''].filter(Boolean).join(' ')}>
        {children}
      </div>
    </Card>
  );
}

export default WorkspaceCardFrame;
